import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";

const LANGS = [
  { code: "zh-TW", label: "繁體" },
  { code: "en",    label: "EN" },
  { code: "ja",    label: "JP" },
  { code: "ko",    label: "KR" },
  { code: "vi",    label: "VI" },
  { code: "th",    label: "TH" },
];

function MobileLangDropdown({
  currentLang,
  onSwitch,
}: {
  currentLang: string;
  onSwitch: (lang: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = LANGS.find((l) => l.code === currentLang) ?? LANGS[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors font-medium"
        style={{ backgroundColor: "var(--border-color)", color: "var(--text-primary)" }}
      >
        <i className="ri-global-line text-sm"></i>
        {current.label}
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? "rotate-180" : ""}`}></i>
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 w-24 rounded-lg overflow-hidden z-50"
          style={{
            backgroundColor: "var(--dropdown-bg)",
            border: "1px solid var(--border-color)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          }}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => { onSwitch(l.code); setOpen(false); }}
              className="flex items-center justify-between w-full px-3 py-2 text-xs transition-colors cursor-pointer"
              style={{
                color: currentLang === l.code ? "var(--accent)" : "var(--text-nav)",
                fontWeight: currentLang === l.code ? 600 : 400,
                backgroundColor: "transparent",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--border-color)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              {l.label}
              {currentLang === l.code && (
                <i className="ri-check-line text-xs" style={{ color: "var(--accent)" }}></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LangSwitcher({
  currentLang,
  onSwitch,
  mobile = false,
}: {
  currentLang: string;
  onSwitch: (lang: string) => void;
  mobile?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGS.find((l) => l.code === currentLang) ?? LANGS[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (mobile) {
    return (
      <div className="flex flex-col">
        {LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => onSwitch(l.code)}
            className="flex items-center justify-between text-sm py-2.5 border-b cursor-pointer whitespace-nowrap transition-colors"
            style={{
              borderColor: "var(--border-color)",
              color: currentLang === l.code ? "var(--accent)" : "var(--text-nav)",
              fontWeight: currentLang === l.code ? 600 : 400,
            }}
          >
            <span>{l.label}</span>
            {currentLang === l.code && (
              <i className="ri-check-line text-sm" style={{ color: "var(--accent)" }}></i>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        style={{ backgroundColor: "var(--border-color)", color: "var(--text-nav)" }}
      >
        <i className="ri-global-line text-sm"></i>
        {current.label}
        <i className={`ri-arrow-down-s-line transition-transform text-xs ${open ? "rotate-180" : ""}`}></i>
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 w-28 rounded-lg overflow-hidden z-50"
          style={{
            backgroundColor: "var(--dropdown-bg)",
            border: "1px solid var(--border-color)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          }}
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => { onSwitch(l.code); setOpen(false); }}
              className="flex items-center justify-between w-full px-3 py-2 text-xs transition-colors cursor-pointer"
              style={{
                color: currentLang === l.code ? "var(--accent)" : "var(--text-nav)",
                backgroundColor: "transparent",
                fontWeight: currentLang === l.code ? 600 : 400,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "var(--border-color)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {l.label}
              {currentLang === l.code && (
                <i className="ri-check-line text-xs" style={{ color: "var(--accent)" }}></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [mobileDownloadOpen, setMobileDownloadOpen] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const switchLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('tupim_lang', lang);
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + "/");

  const navLinkStyle = (active: boolean) => ({
    color: active ? "var(--accent)" : "var(--text-nav)",
  });

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={
        scrolled
          ? {
              backgroundColor: "var(--nav-bg-scrolled)",
              backdropFilter: "blur(12px)",
              borderBottom: "1px solid var(--nav-border)",
            }
          : { backgroundColor: "transparent" }
      }
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/113546dc-a8c1-44fa-8aac-7a04c0cd68d4/a91fc359-a746-41f9-8206-b58c60059de3_unnamed.png?v=fe9bc73b1061e5aa513302540e5292fb"
            alt="TupIM"
            className="w-8 h-8 rounded-lg object-cover"
          />
          <span className="font-bold text-lg tracking-tight" style={{ color: "var(--text-primary)" }}>TupIM</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {/* Home */}
          <Link
            to="/"
            className="text-sm px-3 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            style={{ color: "var(--text-nav)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-nav)")}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            {t("nav.guides")}
          </Link>

          {/* Features */}
          <button
            onClick={() => {
              if (location.pathname !== '/') {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              } else {
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="text-sm px-3 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            style={{ color: "var(--text-nav)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-nav)")}
          >
            {t("nav.clientSdk")}
          </button>

          {/* Docs */}
          <Link
            to="/docs"
            className="text-sm px-3 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            style={{ color: "var(--text-nav)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-nav)")}
          >
            {t("nav.serverApi")}
          </Link>

          {/* Download dropdown */}
          <div className="relative" ref={downloadRef}>
            <button
              onClick={() => setDownloadOpen(!downloadOpen)}
              className="flex items-center gap-1 text-sm px-3 py-2 rounded-md transition-colors cursor-pointer whitespace-nowrap"
              style={{ color: "var(--text-nav)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-nav)")}
            >
              {t("nav.download")}
              <i className={`ri-arrow-down-s-line transition-transform ${downloadOpen ? "rotate-180" : ""}`}></i>
            </button>
            {downloadOpen && (
              <div
                className="absolute top-full left-0 mt-1 w-40 rounded-lg overflow-hidden"
                style={{
                  backgroundColor: "var(--dropdown-bg)",
                  border: "1px solid var(--border-color)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                }}
              >
                <button
                  onClick={() => { navigate("/download/android"); setDownloadOpen(false); }}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm transition-colors cursor-pointer"
                  style={{ color: "var(--text-nav)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--text-primary)";
                    e.currentTarget.style.backgroundColor = "var(--border-color)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "var(--text-nav)";
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <i className="ri-android-line" style={{ color: "var(--accent)" }}></i>
                  {t("nav.android")}
                </button>
                <button
                  onClick={() => { navigate("/download/ios"); setDownloadOpen(false); }}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm transition-colors cursor-pointer"
                  style={{ color: "var(--text-nav)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--text-primary)";
                    e.currentTarget.style.backgroundColor = "var(--border-color)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "var(--text-nav)";
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <i className="ri-apple-line" style={{ color: "var(--text-nav)" }}></i>
                  {t("nav.ios")}
                </button>
                <button
                  onClick={() => { navigate("/download/pc"); setDownloadOpen(false); }}
                  className="flex items-center gap-2 w-full px-4 py-2.5 text-sm transition-colors cursor-pointer"
                  style={{ color: "var(--text-nav)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--text-primary)";
                    e.currentTarget.style.backgroundColor = "var(--border-color)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "var(--text-nav)";
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <i className="ri-computer-line" style={{ color: "var(--accent)" }}></i>
                  {t("nav.pc")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-2">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer"
            style={{
              backgroundColor: "var(--border-color)",
              color: "var(--text-nav)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-nav)")}
            title={theme === "dark" ? "切換白天模式" : "切換夜晚模式"}
          >
            <i className={theme === "dark" ? "ri-sun-line text-sm" : "ri-moon-line text-sm"}></i>
          </button>

          {/* Language switcher */}
          <LangSwitcher currentLang={i18n.language} onSwitch={switchLang} />
        </div>

        {/* Mobile hamburger */}
        <div className="md:hidden flex items-center gap-1.5">
          <button
            onClick={toggleTheme}
            className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer"
            style={{ color: "var(--text-primary)", backgroundColor: "var(--border-color)" }}
          >
            <i className={theme === "dark" ? "ri-sun-line" : "ri-moon-line"}></i>
          </button>
          {/* Mobile language switcher dropdown */}
          <MobileLangDropdown currentLang={i18n.language} onSwitch={switchLang} />
          <button
            className="w-8 h-8 flex items-center justify-center text-xl cursor-pointer"
            style={{ color: "var(--text-primary)" }}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden border-t px-6 py-4 flex flex-col gap-1"
          style={{
            backgroundColor: "var(--mobile-menu-bg)",
            borderColor: "var(--nav-border)",
          }}
        >
          <Link
            to="/"
            className="text-sm py-2.5 cursor-pointer whitespace-nowrap transition-colors border-b"
            style={{ color: "var(--text-nav)", borderColor: "var(--border-color)" }}
            onClick={() => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            {t("nav.guides")}
          </Link>
          <button
            className="text-left text-sm py-2.5 cursor-pointer whitespace-nowrap transition-colors border-b"
            style={{ color: "var(--text-nav)", borderColor: "var(--border-color)" }}
            onClick={() => {
              setMobileOpen(false);
              if (location.pathname !== '/') {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              } else {
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            {t("nav.clientSdk")}
          </button>
          <Link
            to="/docs"
            className="text-sm py-2.5 cursor-pointer whitespace-nowrap transition-colors border-b block"
            style={{ color: "var(--text-nav)", borderColor: "var(--border-color)" }}
            onClick={() => setMobileOpen(false)}
          >
            {t("nav.serverApi")}
          </Link>
          <Link
            to="/about"
            className="text-sm py-2.5 cursor-pointer whitespace-nowrap transition-colors border-b"
            style={{ color: "var(--text-nav)", borderColor: "var(--border-color)" }}
            onClick={() => setMobileOpen(false)}
          >
            {t('nav.about')}
          </Link>
          <Link
            to="/contact"
            className="text-sm py-2.5 cursor-pointer whitespace-nowrap transition-colors border-b"
            style={{ color: "var(--text-nav)", borderColor: "var(--border-color)" }}
            onClick={() => setMobileOpen(false)}
          >
            {t('nav.contact')}
          </Link>

          {/* Download expandable */}
          <div className="border-b" style={{ borderColor: "var(--border-color)" }}>
            <button
              className="w-full text-left text-sm py-2.5 cursor-pointer whitespace-nowrap transition-colors flex items-center justify-between"
              style={{ color: "var(--text-nav)" }}
              onClick={() => setMobileDownloadOpen(!mobileDownloadOpen)}
            >
              <span>{t('nav.download')}</span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileDownloadOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileDownloadOpen && (
              <div className="flex flex-col gap-0 pb-2">
                <Link
                  to="/download/android"
                  className="text-sm py-2 pl-4 cursor-pointer flex items-center gap-2 rounded-md transition-colors"
                  style={{ color: "var(--text-nav)" }}
                  onClick={() => { setMobileOpen(false); setMobileDownloadOpen(false); }}
                >
                  <i className="ri-android-line text-base"></i>
                  Android
                </Link>
                <Link
                  to="/download/ios"
                  className="text-sm py-2 pl-4 cursor-pointer flex items-center gap-2 rounded-md transition-colors"
                  style={{ color: "var(--text-nav)" }}
                  onClick={() => { setMobileOpen(false); setMobileDownloadOpen(false); }}
                >
                  <i className="ri-apple-line text-base"></i>
                  iOS
                </Link>
                <Link
                  to="/download/pc"
                  className="text-sm py-2 pl-4 cursor-pointer flex items-center gap-2 rounded-md transition-colors"
                  style={{ color: "var(--text-nav)" }}
                  onClick={() => { setMobileOpen(false); setMobileDownloadOpen(false); }}
                >
                  <i className="ri-computer-line text-base"></i>
                  PC
                </Link>
              </div>
            )}
          </div>

        </div>
      )}
    </nav>
  );
}